import { z } from 'zod';

/**
 * Wire schemas for user API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Booleans as integers (0/1) in D1 database
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 */

/**
 * User wire schema (matches backend response format)
 * Based on UserReadSchema from backend
 * Email is optional for public user responses (search/list endpoints)
 */
export const userWireSchema = z.object({
  id: z.string(),
  email: z.string().email().optional(), // Optional for public user responses
  name: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  display_username: z.string().nullable().optional(),
  image: z.string().nullable().optional(), // Can be null
  bio: z.string().nullable().optional(), // Can be null
  role: z.string().default('user'),
  email_verified: z.number().int().min(0).max(1).default(0), // D1 stores as 0/1, not boolean
  created_at: z.string(), // D1 DATETIME (ISO format), not Unix number
  updated_at: z.string(), // D1 DATETIME (ISO format), not Unix number
  banned: z.number().int().min(0).max(1).default(0), // D1 stores as 0/1, not boolean
  // Legacy fields for compatibility
  avatar: z.string().nullable().optional(),
  followers: z.number().optional(),
  following: z.number().optional(),
  checkins: z.number().optional(),
});

export type UserWire = z.infer<typeof userWireSchema>;

/**
 * Paginated user search response schema
 * Matches createPaginatedResponseSchema(UserPublicReadSchema) from backend
 */
export interface PaginatedUserResponse {
  items: UserWire[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}
